import { FC } from 'react';
import { useTranslation } from 'react-i18next';

import { CursorClickOutline, Flex, HStack, Icon, PlantOutline, PresentationChartLineOutline, Text } from '@bonusly/design-system';

interface BizyAdminEmptyStateProps {
	/** Body/description font size. Parent passes `fullscreenFontSize` when in fullscreen mode. */
	fontSize?: string;
}

/**
 * Full first-launch introduction shown to admins in the Bizy side panel.
 *
 * Rendered when the greeting variant is still `introductory` (the first four panel
 * opens — see `useBizyGreeting`). Mirrors the 1:1 `BizyEmptyState` shape so the two
 * intro surfaces stay visually consistent.
 */
export const BizyAdminEmptyState: FC<BizyAdminEmptyStateProps> = ({ fontSize = 'sm' }) => {
	const { t } = useTranslation('admin_bizy', { keyPrefix: 'bizy_admin_intro' });

	return (
		<Flex direction="column" gap={5} px={2}>
			<Flex direction="column" gap={2}>
				<Text fontSize="md" fontWeight="semibold" color="text.default">
					{t('greeting')}
				</Text>
				<Text fontSize={fontSize} color="text.soft" lineHeight="tall">
					{t('introduction')}
				</Text>
			</Flex>

			<Flex direction="column" gap={4}>
				<Text fontSize={fontSize} fontWeight="bold" color="text.default">
					{t('capabilities_header')}
				</Text>
				<Flex direction="column" gap={4}>
					<CapabilityRow icon={PresentationChartLineOutline} title={t('search_title')} description={t('search_description')} fontSize={fontSize} />
					<CapabilityRow icon={CursorClickOutline} title={t('suggestions_title')} description={t('suggestions_description')} fontSize={fontSize} />
					<CapabilityRow icon={PlantOutline} title={t('grow_title')} description={t('grow_description')} fontSize={fontSize} iconStrokeWidth={2} />
				</Flex>
			</Flex>

			<Text fontSize={fontSize} color="text.soft">
				{t('cta')}
			</Text>
		</Flex>
	);
};

BizyAdminEmptyState.displayName = 'BizyAdminEmptyState';

interface CapabilityRowProps {
	icon: typeof PresentationChartLineOutline;
	title: string;
	description: string;
	fontSize: string;
	iconStrokeWidth?: number;
}

const CapabilityRow: FC<CapabilityRowProps> = ({ icon, title, description, fontSize, iconStrokeWidth }) => (
	<HStack spacing={3} align="flex-start">
		<Icon as={icon} boxSize={5} color="text.soft" flexShrink={0} mt={0.5} strokeWidth={iconStrokeWidth} />
		<Flex direction="column">
			<Text fontWeight="semibold" fontSize={fontSize} color="text.default">
				{title}
			</Text>
			<Text fontSize={fontSize} color="text.soft">
				{description}
			</Text>
		</Flex>
	</HStack>
);
